/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import { API_BASE_URL } from "../config/api.config";
import { logger } from "../utils/logger";

export type RequestConfig = AxiosRequestConfig;

const instance: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  // Auth is httpOnly cookies set by Web/api. Nothing in this app ever holds
  // a JWT, so this flag is the entire authentication mechanism — dropping it
  // logs every user out silently.
  withCredentials: true,
  timeout: 30000,
  // No default Content-Type: axios sets application/json for object bodies on
  // its own, and a forced JSON header makes it serialize FormData to JSON —
  // the avatar upload then reaches multer with no file ("File is required").
});

instance.interceptors.response.use(
  (response) => {
    const payload: any = response?.data;
    if (
      payload &&
      typeof payload === "object" &&
      "success" in payload &&
      "data" in payload
    ) {
      return { ...response, data: payload.data };
    }
    return response;
  },
  (error: AxiosError) => {
    // Unlike Frontend/portal, a 401 does not redirect to /login here. Most of
    // this site is public, and the profile probe on every page load 401s for
    // anonymous visitors — redirecting would bounce them off /pricing.
    const method = error.config?.method?.toUpperCase() ?? "?";
    const url = error.config?.url ?? "?";
    const status = error.response?.status ?? "network";
    logger.error(`API ${method} ${url} → ${status}`);
    return Promise.reject(error);
  },
);

export const api = {
  request: <T = any>(config: RequestConfig): Promise<AxiosResponse<T>> =>
    instance.request<T>(config),
};

export default instance;
